import {
  Component,
  HostBinding,
  signal,
  inject,
  OnInit,
  Signal,
  viewChild,
  AfterViewInit,
  ChangeDetectionStrategy,
  TemplateRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { MatFormField } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { HttpErrorResponse } from '@angular/common/http';
import { MatCheckboxModule } from '@angular/material/checkbox';

import {
  IconComponent,
  ProgressComponent,
  MessageService
} from '@factor_ec/ui';
import { GoogleTagManagerService, StorageService } from '@factor_ec/utils';
import moment from 'moment';

import { AppService } from '@/core/app.service';
import { SessionService } from '@/core/session.service';
import { ForgotPasswordComponent } from '@/core/components/forgot-password/forgot-password.component';
import { PageComponent } from '@/core/components/page/page.component';
import { Currency } from '@/main/models/currency';
import { environment } from '@/environments/environment';
import { ApiService } from '@/core/api.service';
import { ErrorMessageService } from '@/core/error-message.service';
import { aliasValidator } from '@/shared/alias.validator';
import { AuthService } from '@auth0/auth0-angular';
import { Browser } from '@capacitor/browser';

/**
 * @description Componente para manejar la autenticación de usuarios
 * @class AuthComponent
 * @implements {OnInit, AfterViewInit}
 */
@Component({
  selector: 'app-auth',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormField,
    MatInputModule,
    MatDialogModule,
    MatMenuModule,
    MatCheckboxModule,
    MatSelectModule,
    RouterModule,
    IconComponent,
    ProgressComponent
  ],
  templateUrl: './auth.component.html',
  styleUrl: './auth.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuthComponent implements OnInit, AfterViewInit {
  // Dependency injection
  appService = inject(AppService);
  apiService = inject(ApiService);
  sessionService = inject(SessionService);
  private dialog = inject(MatDialog);
  private errorMessageService = inject(ErrorMessageService);
  private formBuilder = inject(FormBuilder);
  private googleTagManagerService = inject(GoogleTagManagerService);
  private messageService = inject(MessageService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private storageService = inject(StorageService);
  private title = inject(Title);
  private auth = inject(AuthService);

  // Properties
  currentFeatureIndex = signal<number>(0);
  mode = signal<string>('');
  passwordVisible = signal<boolean>(false);
  signinForm: FormGroup = new FormGroup({});
  signupForm: FormGroup = new FormGroup({});
  changePasswordForm: FormGroup = new FormGroup({});
  submitting = signal<boolean>(false);
  lastUser = signal<any>(undefined);
  errorMessage = signal<string>('');
  notEqualMessage = $localize`New password is not the same`;
  countries = signal<Currency[]>([]);
  appUsagePolicy = signal<string>(environment.appUsagePolicyUrl);
  dataProtectionPolicy = signal<string>(environment.dataProtectionPolicyUrl);
  class: string = '';

  readonly signupConfirmationTemplate =
    viewChild.required<TemplateRef<unknown>>('signupConfirmationTemplate');

  @HostBinding('class') get hostClasses(): string {
    return ['ft-auth', this.mode() ? 'ft-auth--form' : null, this.class].join(
      ' '
    );
  }

  /**
   * @description Constructor del componente
   * @constructor
   */
  constructor() {
    this.initializeForms();
  }

  /**
   * @description Inicializa el componente
   * @implements OnInit
   */
ngOnInit(): void {
  let mode: string = this.route.snapshot.data['mode'];

  this.setMode(mode);
  if (this.lastUser) {
    this.signinForm.patchValue({ email: this.lastUser().email });
  }
}

     login() {
    this.auth
      .loginWithRedirect({
        async openUrl(url: string) {
          return Browser.open({ url, windowName: '_self' });
        }
      })
      .subscribe();
  }

  /**
   * @description Inicializa la vista del componente
   * @implements AfterViewInit
   */
  ngAfterViewInit(): void {
    this.handleSignupConfirmation();
  }

  /**
   * @description Cambia la contraseña del usuario
   * @returns {Promise<void>}
   */
  async changePassword(): Promise<void> {
    if (this.changePasswordForm.valid) {
      try {
        this.submitting.set(true);
        this.changePasswordForm.disable();
        const body = {
          email: this.signinForm.value.email,
          currentPassword: this.signinForm.value.password,
          newPassword: this.changePasswordForm.value.newPassword
        };
        const response: any = await this.apiService.call({
          microServiceName: 'WSMPUsersManagement',
          resourcePath: `/user/change-password-by-email`,
          method: 'POST',
          useToken: true,
          body,
          bodyEncrypted: true
        });
        this.messageService.show(
          $localize`Your password was updated successfully.`,
          { class: 'ft-message--success', icon: 'check--circle' }
        );
        this.signinForm.get('password')?.setValue('');
        this.mode.set('signin');
      } catch (err: any) {
        this.submitting.set(false);
        this.changePasswordForm.enable();
        throw err;
      } finally {
        this.submitting.set(false);
      }
    }
  }

  /**
   * @description Validador de confirmación de contraseña
   * @param {AbstractControl} control - Control a validar
   * @returns {ValidationErrors | null} Errores de validación
   */
  confirmPasswordValidator(control: AbstractControl): ValidationErrors | null {
    let value: { [key: string]: any } | null = null;
    if (
      control &&
      control.parent &&
      control.parent.get('password')?.value !== control.value
    ) {
      value = { notEqual: true, fieldName: $localize`Password` };
    }
    return value;
  }

  /**
   * @description Validador de confirmación de nueva contraseña
   * @param {AbstractControl} control - Control a validar
   * @returns {ValidationErrors | null} Errores de validación
   */
  confirmNewPasswordValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    let value: { [key: string]: any } | null = null;
    if (
      control &&
      control.parent &&
      control.parent.get('newPassword')?.value !== control.value
    ) {
      value = { notEqual: true, fieldName: $localize`New password` };
    }
    return value;
  }

  /**
   * @description Olvida el usuario almacenado
   */
  forgetUser(): void {
    this.storageService.delete(this.sessionService.lastUserKey, 'local');
    this.lastUser.set(undefined);
    this.signinForm.patchValue({ email: '', password: '' });
  }

  /**
   * @description Abre el diálogo de contraseña olvidada
   */
  forgotPassword(): void {
    this.dialog.open(ForgotPasswordComponent, {
      panelClass: 'ft-dialog',
      width: '400px'
    });
  }

  /**
   * @description Inicializa los formularios
   * @private
   */
  private initializeForms(): void {
    this.signinForm = this.formBuilder.group({
      email: [
        '',
        [Validators.required, Validators.minLength(4), Validators.email]
      ],
      password: ['', [Validators.required, Validators.minLength(8)]]
    });
    this.signupForm = this.formBuilder.group({
      alias: ['', [Validators.required, aliasValidator()]],
      email: ['', [Validators.required, Validators.email]],
      country: ['', Validators.required],
      termsAccepted: [false, Validators.requiredTrue],
      password: ['', [Validators.required, this.passwordValidator()]],
      confirmPassword: [
        '',
        [Validators.required, this.confirmPasswordValidator]
      ]
    });
    this.changePasswordForm = this.formBuilder.group({
      newPassword: ['', [Validators.required, this.passwordValidator()]],
      confirmPassword: [
        '',
        [Validators.required, this.confirmNewPasswordValidator]
      ]
    });
    this.lastUser = this.storageService.get(
      this.sessionService.lastUserKey,
      'local'
    );
  }

  /**
   * @description Maneja la confirmación de registro
   * @private
   */
  private handleSignupConfirmation(): void {
    // Confirmación de registro
    const signupConfirmation = this.route.snapshot.queryParamMap.get('sc');
    if (signupConfirmation) {
      this.dialog.open(this.signupConfirmationTemplate(), {
        panelClass: 'ft-dialog',
        width: '400px'
      });
    }
  }

  /**
   * @description Abre una página en un diálogo
   * @param {string} url - URL de la página
   */
  openPage(url: string): void {
    this.dialog.open(PageComponent, {
      data: { url },
      panelClass: ['ft-dialog', 'ft-dialog--stacked'],
      height: '100vh',
      width: '600px',
      position: {
        left: 'auto',
        right: '0'
      }
    });
  }

  /**
   * @description Validador de contraseña
   * @returns {ValidatorFn} Función validadora
   */
  passwordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: string = control.value || '';

      // Reglas de validación
      const hasMinLength = value.length >= 8;
      const hasUpperCase = /[A-Z]/.test(value);
      const hasLowerCase = /[a-z]/.test(value);
      const hasSpecialCharacter = /[-_!@#$%^&*(),.?":{}|<>]/.test(value);
      const hasNumber = /\d/.test(value);

      // Si no cumple alguna regla, devolver errores específicos
      const errors: any = {};
      if (!hasMinLength)
        errors.minLength = $localize`Must contain at least 8 characters`;
      if (!hasUpperCase)
        errors.upperCase = $localize`Must contain at least 1 capital letter`;
      if (!hasLowerCase)
        errors.lowerCase = $localize`Must contain at least 1 lowercase letter`;
      if (!hasSpecialCharacter)
        errors.specialCharacter = $localize`Must contain at least 1 special character`;
      if (!hasNumber) errors.number = $localize`Must contain 1 number`;

      // Retornar errores si hay alguno, o null si todo está bien
      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  /**
   * @description Establece el índice de característica actual
   * @param {boolean} event - Estado del evento
   * @param {number} index - Índice a establecer
   */
  setCurrentFeatureIndex(event: boolean, index: number): void {
    if (event) {
      this.currentFeatureIndex.set(index);
    }
  }

  /**
   * @description Establece el modo de autenticación
   * @param {string} mode - Modo a establecer
   * @returns {Promise<void>}
   */
  async setMode(mode: string): Promise<void> {
    this.mode.set(mode);
    switch (mode) {
      case 'signin':
        this.title.setTitle($localize`Sign in`);
        break;
      case 'signup':
        this.title.setTitle($localize`Sign up`);
        this.countries.set(
          this.sessionService
            .catalogs()
            ?.filter(c => c.catalogId === 'CPAMD')
            ?.map(c => {
              return {
                id: c.id,
                description: c.description
              } as Currency;
            }) || []
        );
        break;
      default:
        this.title.setTitle($localize`Start`);
        break;
    }
    this.googleTagManagerService.addVariable({
      event: 'page_view',
      page_title: this.title.getTitle()
    });
  }

  /**
   * @description Envía el formulario de inicio de sesión
   * @returns {Promise<void>}
   */
  async submitSignin(): Promise<void> {
    if (this.signinForm.valid) {
      this.errorMessage.set('');
      this.signinForm.disable();
      this.submitting.set(true);
      try {
        await this.sessionService.signin(this.signinForm.value);
        this.router.navigateByUrl('/');
      } catch (err: any) {
        this.signinForm.enable();
        this.submitting.set(false);
        if (err instanceof HttpErrorResponse) {
          switch (err.error.code) {
            case '50156':
              this.mode.set('change-password');
              return;
          }
        }
      }
    }
  }

  /**
   * @description Envía el formulario de registro
   * @returns {Promise<void>}
   */
  async submitSignup(): Promise<void> {
    if (this.signupForm.valid) {
      try {
        this.errorMessage.set('');
        this.submitting.set(true);
        this.signupForm.disable();
        await this.sessionService.signup({
          alias: this.signupForm.value.alias,
          countryId: this.signupForm.value.country.id,
          email: this.signupForm.value.email,
          password: this.signupForm.value.password,
          termsAccepted: this.signupForm.value.termsAccepted,
          acceptanceDateTime: moment().toISOString(),
          acceptedPoliciesUrls: [this.appUsagePolicy(), this.dataProtectionPolicy()]
        });
        this.router.navigate(
          ['/signup-success', { email: this.signupForm.value.email }],
          {
            skipLocationChange: true
          }
        );
      } catch (err: any) {
        this.signupForm.enable();
        this.submitting.set(false);
        throw err;
      }
    }
  }

  /**
   * @description Alterna la visibilidad de la contraseña
   */
  togglePasswordVisible(): void {
    this.passwordVisible.set(!this.passwordVisible());
  }
}
